import { useState } from 'react';

import {
  type ErrorLike,
  type InternalRefetchQueriesInclude,
} from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  SwipeableDrawer,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { type PropertyFileFragment } from '@graphql/__generated__/graphql';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { FileTypeAutocomplete } from '@modules/_shared/components/FileTypeAutocomplete';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
  isMimetypeAllowedForPublication,
  isVideoFile,
} from '@modules/_shared/components/PropertyFiles';
import { Puller } from '@modules/_shared/components/puller';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import {
  UPDATE_FILE,
  UPDATE_PROPERTY_FILE,
} from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

const FilePreview = ({ file }: { file: PropertyFileFragment['file'] }) => {
  const { t } = useLocale();
  const url = file.url ?? '';
  const mimetype = file.mimetype ?? '';
  const isImage = mimetype.startsWith('image/');
  const isVideo = isVideoFile(file);
  const isPdf = mimetype === 'application/pdf';

  if (isImage) {
    return (
      <Box
        component="img"
        src={url}
        alt={file.name}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 1,
          objectFit: 'contain',
        }}
      />
    );
  }

  if (isVideo) {
    return (
      <Box
        component="video"
        src={url}
        controls
        sx={{
          maxWidth: '100%',
          maxHeight: { lg: '60vh' },
          borderRadius: 1,
        }}
      />
    );
  }

  if (isPdf) {
    return (
      <Box
        component="iframe"
        title={file.name}
        src={`${url}#pagemode=none&navpanes=0`}
        sx={{ width: '100%', height: '100%', border: 0, borderRadius: 1 }}
      />
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{ alignItems: 'center', py: 4, color: 'text.secondary' }}
    >
      <FileIcon mimetype={mimetype} filename={file.name} size="large" />
      <Typography variant="body2">{t('No preview available')}</Typography>
    </Stack>
  );
};

const ToggleControl = ({
  label,
  checked,
  disabled,
  loading = false,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  loading?: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <FormControlLabel
    sx={{ m: 0, gap: 0.5, opacity: loading ? 0.5 : 1 }}
    labelPlacement="end"
    disabled={disabled || loading}
    control={
      <Switch
        size="small"
        checked={checked}
        disabled={disabled || loading}
        onChange={evt => onChange(evt.target.checked)}
      />
    }
    label={
      <Typography
        variant="body2"
        sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}
      >
        {label}
      </Typography>
    }
  />
);

const FileDetailContent = ({
  propertyFile,
  currentIndex,
  files,
  propertyId,
  readOnly,
  refetchQueries,
}: {
  propertyFile: PropertyFileFragment;
  currentIndex: number;
  files: PropertyFileFragment[];
  propertyId: string;
  readOnly: boolean;
  refetchQueries?: InternalRefetchQueriesInclude;
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);

  const [updateFile, { loading: docTypeLoading }] = useMutation(UPDATE_FILE, {
    refetchQueries,
    onError: setMutationError,
  });
  const [updatePropertyFile, { loading: brochureLoading }] = useMutation(
    UPDATE_PROPERTY_FILE,
    { refetchQueries, onError: setMutationError },
  );
  const [updateDisplayOnListing, { loading: portalLoading }] = useMutation(
    UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
    { refetchQueries, onError: setMutationError },
  );

  const { file } = propertyFile;
  const isVideo = isVideoFile(file);
  const isVideoDocumentType = file.document_type?.name === 'video';
  const canAttachToBrochure =
    file.mimetype === 'application/pdf' ||
    (file.mimetype?.startsWith('image/') ?? false);
  const uploaderName =
    file.uploader != null
      ? [file.uploader.first_name, file.uploader.last_name]
          .filter(Boolean)
          .join(' ')
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flexShrink: 0, p: 2 }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {uploaderName != null
              ? t('Uploaded by {{name}}', { name: uploaderName })
              : t('Uploaded')}{' '}
            <TimeAgo dateString={file.created_at} addSuffix />
          </Typography>
          {files.length > 1 && (
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                aria-label={t('Previous')}
                disabled={currentIndex <= 0}
                onClick={() =>
                  navigate(`./${files[currentIndex - 1].file.id}`, {
                    replace: true,
                  })
                }
              >
                <ChevronLeft size={18} />
              </IconButton>
              <IconButton
                size="small"
                aria-label={t('Next')}
                disabled={currentIndex >= files.length - 1}
                onClick={() =>
                  navigate(`./${files[currentIndex + 1].file.id}`, {
                    replace: true,
                  })
                }
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1,
          }}
        >
          {!isVideo && (
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '1 1 180px' },
                minWidth: { xs: '100%', lg: 160 },
              }}
            >
              <FileTypeAutocomplete
                types={['property']}
                placeholder={t('Document type')}
                value={file.document_type ?? undefined}
                onChange={(_, value) => {
                  if (!readOnly) {
                    updateFile({
                      variables: {
                        id: file.id,
                        input: { document_type_id: value.id },
                      },
                    });
                  }
                }}
                disabled={readOnly || docTypeLoading}
              />
            </Box>
          )}

          {!isVideo && (
            <ToggleControl
              label={t('Attach to brochure')}
              checked={propertyFile.is_visible}
              disabled={readOnly || !canAttachToBrochure}
              loading={brochureLoading}
              onChange={checked =>
                updatePropertyFile({
                  variables: {
                    file_id: file.id,
                    property_id: propertyId,
                    input: { is_visible: checked },
                  },
                })
              }
            />
          )}

          <ToggleControl
            label={t('Display on portals')}
            checked={propertyFile.display_on_listing}
            loading={portalLoading}
            disabled={
              readOnly || !isMimetypeAllowedForPublication(file.mimetype)
            }
            onChange={checked =>
              updateDisplayOnListing({
                variables: {
                  file_id: file.id,
                  property_id: propertyId,
                  display: checked,
                  should_reset_non_video: checked && !isVideoDocumentType,
                  should_reset_video: checked && isVideoDocumentType,
                },
              })
            }
          />
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 0, lg: 2 },
          overflow: 'auto',
        }}
      >
        <FilePreview key={file.id} file={file} />
      </Box>

      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </Box>
  );
};

export const FileDetailPanel = ({
  propertyId,
  files,
  selectedFileId,
  readOnly = false,
  refetchQueries,
}: {
  propertyId: string;
  files: PropertyFileFragment[];
  selectedFileId?: string;
  readOnly?: boolean;
  refetchQueries?: InternalRefetchQueriesInclude;
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const currentIndex = files.findIndex(item => item.file.id === selectedFileId);
  const current = files[currentIndex] ?? null;

  const content =
    current != null ? (
      <FileDetailContent
        propertyFile={current}
        currentIndex={currentIndex}
        files={files}
        propertyId={propertyId}
        readOnly={readOnly}
        refetchQueries={refetchQueries}
      />
    ) : null;

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={current != null}
        onClose={() => navigate('.', { replace: true })}
        onOpen={() => {}}
        sx={{
          '& > .MuiPaper-root': {
            height: '90%',
            overflow: 'hidden',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1,
            flexShrink: 0,
          }}
        >
          <Puller />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>{content}</Box>
      </SwipeableDrawer>
    );
  }

  if (content == null) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">{t('Select a file to preview')}</Typography>
      </Stack>
    );
  }

  return content;
};
